import { motion, AnimatePresence } from "framer-motion";

/**
 * An accordion section with a title, count/total label, progress bar, and animated
 * expand/collapse of its children via framer-motion.
 *
 * @param {{ title: string, id: string, count: number, total: number, isExpanded: boolean, onToggle: (id: string) => void, children: React.ReactNode }} props
 * @param {string} props.title - Section heading text.
 * @param {string} props.id - Unique identifier used as the toggle callback argument.
 * @param {number} props.count - Number of unlocked/completed items.
 * @param {number} props.total - Total items in this section.
 * @param {boolean} props.isExpanded - Whether the section body is currently visible.
 * @param {(id: string) => void} props.onToggle - Callback invoked with the section id on header click.
 * @param {React.ReactNode} props.children - Content revealed when expanded.
 * @returns {JSX.Element} The collapsible section component.
 */
const CollapsibleSection = ({ title, id, count, total, isExpanded, onToggle, children }) => {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div className={`collapsible-section ${isExpanded ? "expanded" : ""}`}>
      <div className="collapsible-header" onClick={() => onToggle(id)}>
        <div className="collapsible-header-row">
          <h4 className="collapsible-title">
            {title} <span className="collapsible-count">({count}/{total})</span>
          </h4>
          <span className="chevron">▼</span>
        </div>
        <div className="collapsible-progress-track">
          <div className="collapsible-progress-fill" style={{ width: `${pct}%` }} />
        </div>
      </div>
      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            style={{ overflow: "hidden" }}
          >
            <div className="collapsible-content">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CollapsibleSection;
